import { Navigate } from "react-router-dom";
import { useRoutePermission } from "@/features/auth";
import { PermissionAction, PermissionResource } from "@/entities/user";
import useMisc from "@/shared/hooks/useMisc";

interface RequirePermissionProps {
  children: React.ReactNode;
  resource?: PermissionResource;
  action?: PermissionAction;
  roles?: string[];
  requireAll?: boolean;
  permissions?: Array<{ resource: PermissionResource; action: PermissionAction }>;
}

/**
 * Component to protect routes based on permissions
 * @param resource - Single resource to check (e.g., 'user', 'product')
 * @param action - Single action to check (default: 'read')
 * @param roles - Array of roles allowed to access the route
 * @param permissions - Array of permission checks (for multiple permissions)
 * @param requireAll - If true, user must have all permissions. If false, user needs any permission (default: false)
 * @param children - Child components to render if permission is granted
 */
export default function RequirePermission({
  children,
  resource,
  action = 'read',
  roles,
  permissions,
  requireAll = false,
}: RequirePermissionProps) {
  const {
    canAccessRoute,
    canAccessRouteByRole,
    canAccessRouteWithAllPermissions,
    canAccessRouteWithAnyPermission,
    isAuthenticated,
    hasActiveStore,
  } = useRoutePermission();
const userData=useMisc();
const isSuperAdmin= userData?.profileData?.data?.user?.isSuperAdmin;
  // Check if user is authenticated
  if (!isAuthenticated() ) {
    return <Navigate to="/unauthorized_access" replace />;
  }

  // Super admin bypasses all route/store/role/permission checks
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check if store is active
  if (!hasActiveStore()) {
    return <Navigate to="/store_inactive" replace />;
  }

  // Check role-based access
  if (roles && roles.length > 0) {
    if (!canAccessRouteByRole(roles)) {
      return <Navigate to="/unauthorized_access" replace />;
    }
    return <>{children}</>;
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? canAccessRouteWithAllPermissions(permissions)
      : canAccessRouteWithAnyPermission(permissions);

    if (!hasAccess) {
      return <Navigate to="/unauthorized_access" replace />;
    }
    return <>{children}</>;
  }

  // Check single resource permission
  if (resource) {
    if (!canAccessRoute(resource, action)) {
      return <Navigate to="/unauthorized_access" replace />;
    }
    return <>{children}</>;
  }

  // If no permission check is specified, just check authentication
  return <>{children}</>;
}
